import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Divider,
    FormControlLabel,
    LinearProgress,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { supabase } from "../../lib/supabaseClient";
import { createNews, getNewsById, updateNews } from "./newsApi";

const NEWS_IMAGE_BUCKET = "news-images";

const colors = {
    cream: "#fffaf2",
    paper: "rgba(255, 250, 242, 0.94)",
    white: "rgba(255,255,255,0.72)",
    ink: "#2a2621",
    muted: "rgba(42, 38, 33, 0.66)",
    softMuted: "rgba(42, 38, 33, 0.48)",
    brown: "#9b7653",
    brownDark: "#6f5136",
    brownDarker: "#543b26",
    border: "rgba(155,118,83,0.16)",
    success: "#1f6f3f",
    successBg: "rgba(31,111,63,0.1)",
    danger: "#9d2b2b",
    dangerBg: "rgba(157,43,43,0.08)",
};

function createSlug(value) {
    return value
        .toLowerCase()
        .trim()
        .replace(/ä/g, "ae")
        .replace(/ö/g, "oe")
        .replace(/ü/g, "ue")
        .replace(/ß/g, "ss")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
}

function getFileExtension(fileName) {
    return fileName.split(".").pop()?.toLowerCase() || "jpg";
}

function createSafeFileName(file) {
    const extension = getFileExtension(file.name);
    const originalName = file.name.replace(/\.[^/.]+$/, "");
    const safeName = createSlug(originalName) || "bild";

    return `${Date.now()}-${safeName}.${extension}`;
}

const emptyForm = {
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    image_url: "",
    published: false,
};

function AdminNewsForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = Boolean(id);
    const fileInputRef = useRef(null);

    const [form, setForm] = useState(emptyForm);
    const [selectedImageFile, setSelectedImageFile] = useState(null);
    const [localPreviewUrl, setLocalPreviewUrl] = useState("");
    const [isLoading, setIsLoading] = useState(isEditMode);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const pageTitle = useMemo(() => {
        return isEditMode ? "Beitrag bearbeiten" : "Neuen Beitrag erstellen";
    }, [isEditMode]);

    const imagePreviewUrl = localPreviewUrl || form.image_url;

    useEffect(() => {
        if (!isEditMode) return;

        async function loadNewsItem() {
            try {
                setIsLoading(true);
                setErrorMessage("");

                const data = await getNewsById(id);

                setForm({
                    title: data.title || "",
                    slug: data.slug || "",
                    excerpt: data.excerpt || "",
                    content: data.content || "",
                    image_url: data.image_url || "",
                    published: Boolean(data.published),
                });
            } catch (error) {
                setErrorMessage("Beitrag konnte nicht geladen werden.");
                console.error(error);
            } finally {
                setIsLoading(false);
            }
        }

        loadNewsItem();
    }, [id, isEditMode]);

    useEffect(() => {
        return () => {
            if (localPreviewUrl) {
                URL.revokeObjectURL(localPreviewUrl);
            }
        };
    }, [localPreviewUrl]);

    function updateField(name, value) {
        setForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    }

    function handleTitleChange(value) {
        setForm((prev) => ({
            ...prev,
            title: value,
            slug: prev.slug ? prev.slug : createSlug(value),
        }));
    }

    function handleImageChange(event) {
        const file = event.target.files?.[0];

        if (!file) return;

        const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
        const maxFileSize = 5 * 1024 * 1024;

        if (!allowedTypes.includes(file.type)) {
            setErrorMessage("Bitte nur JPG, PNG oder WebP hochladen.");
            return;
        }

        if (file.size > maxFileSize) {
            setErrorMessage("Das Bild darf maximal 5 MB groß sein.");
            return;
        }

        if (localPreviewUrl) {
            URL.revokeObjectURL(localPreviewUrl);
        }

        setErrorMessage("");
        setSelectedImageFile(file);
        setLocalPreviewUrl(URL.createObjectURL(file));
    }

    function handleRemoveImage() {
        if (localPreviewUrl) {
            URL.revokeObjectURL(localPreviewUrl);
        }

        setSelectedImageFile(null);
        setLocalPreviewUrl("");
        updateField("image_url", "");

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    }

    async function uploadSelectedImage() {
        if (!selectedImageFile) {
            return form.image_url.trim() || null;
        }

        setIsUploadingImage(true);

        const folderName = createSlug(form.slug || form.title) || "beitrag";
        const fileName = createSafeFileName(selectedImageFile);
        const filePath = `${folderName}/${fileName}`;

        const { error: uploadError } = await supabase.storage
            .from(NEWS_IMAGE_BUCKET)
            .upload(filePath, selectedImageFile, {
                cacheControl: "3600",
                contentType: selectedImageFile.type,
                upsert: false,
            });

        if (uploadError) {
            setIsUploadingImage(false);
            throw uploadError;
        }

        const { data } = supabase.storage
            .from(NEWS_IMAGE_BUCKET)
            .getPublicUrl(filePath);

        setIsUploadingImage(false);

        return data.publicUrl;
    }

    async function handleSubmit(event) {
        event.preventDefault();

        if (!form.title.trim()) {
            setErrorMessage("Bitte einen Titel eingeben.");
            return;
        }

        if (!form.slug.trim()) {
            setErrorMessage("Bitte einen Slug eingeben.");
            return;
        }

        try {
            setIsSubmitting(true);
            setErrorMessage("");

            const uploadedImageUrl = await uploadSelectedImage();

            const payload = {
                title: form.title.trim(),
                slug: createSlug(form.slug),
                excerpt: form.excerpt.trim() || null,
                content: form.content.trim() || null,
                image_url: uploadedImageUrl,
                published: form.published,
                published_at: form.published ? new Date().toISOString() : null,
            };

            if (isEditMode) {
                await updateNews(id, payload);
            } else {
                await createNews(payload);
            }

            navigate("/admin/news");
        } catch (error) {
            if (error.message?.includes("duplicate key")) {
                setErrorMessage(
                    "Dieser Slug existiert bereits. Bitte einen anderen Slug verwenden."
                );
            } else if (
                error.message?.toLowerCase().includes("bucket") ||
                error.message?.toLowerCase().includes("storage") ||
                error.message?.toLowerCase().includes("policy")
            ) {
                setErrorMessage(
                    "Bild konnte nicht hochgeladen werden. Bitte Supabase Storage Bucket und Policies prüfen."
                );
            } else {
                setErrorMessage("Beitrag konnte nicht gespeichert werden.");
            }

            console.error(error);
        } finally {
            setIsSubmitting(false);
            setIsUploadingImage(false);
        }
    }

    if (isLoading) {
        return (
            <Paper
                elevation={0}
                sx={{
                    py: 8,
                    px: 3,
                    borderRadius: 5,
                    backgroundColor: colors.paper,
                    border: `1px solid ${colors.border}`,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                }}
            >
                <CircularProgress sx={{ color: colors.brownDark }} />

                <Typography
                    sx={{
                        fontFamily: '"Nunito Sans", sans-serif',
                        fontSize: 15,
                        fontWeight: 800,
                        color: colors.muted,
                    }}
                >
                    Beitrag wird geladen...
                </Typography>
            </Paper>
        );
    }

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: {
                    xs: 3,
                    md: 4,
                },
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    position: "relative",
                    overflow: "hidden",
                    p: {
                        xs: 3,
                        sm: 4,
                        md: 5,
                    },
                    borderRadius: {
                        xs: 4,
                        md: 6,
                    },
                    border: `1px solid ${colors.border}`,
                    background:
                        "radial-gradient(circle at 88% 14%, rgba(155,118,83,0.18) 0%, rgba(155,118,83,0) 30%), linear-gradient(145deg, rgba(255,250,242,0.96) 0%, rgba(247,241,232,0.92) 100%)",
                    boxShadow:
                        "0 24px 80px rgba(78,54,30,0.1), 0 2px 12px rgba(78,54,30,0.05)",
                }}
            >
                <Box
                    aria-hidden="true"
                    sx={{
                        position: "absolute",
                        width: 260,
                        height: 260,
                        borderRadius: "50%",
                        right: -90,
                        top: -110,
                        backgroundColor: "rgba(155,118,83,0.12)",
                        filter: "blur(8px)",
                    }}
                />

                <Box
                    sx={{
                        position: "relative",
                        zIndex: 1,
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            md: "1.25fr auto",
                        },
                        gap: {
                            xs: 3,
                            md: 5,
                        },
                        alignItems: "center",
                    }}
                >
                    <Box>
                        <Typography
                            component="p"
                            sx={{
                                mb: 1,
                                fontFamily: '"Nunito Sans", sans-serif',
                                fontSize: 13,
                                fontWeight: 900,
                                letterSpacing: "0.16em",
                                textTransform: "uppercase",
                                color: colors.brown,
                            }}
                        >
                            Aktuelles
                        </Typography>

                        <Typography
                            component="h1"
                            sx={{
                                mb: 1.5,
                                fontFamily: '"Quicksand", sans-serif',
                                fontSize: {
                                    xs: "2.35rem",
                                    sm: "3rem",
                                    md: "3.65rem",
                                },
                                lineHeight: 0.95,
                                fontWeight: 800,
                                letterSpacing: "-0.06em",
                                color: colors.ink,
                            }}
                        >
                            {pageTitle}.
                        </Typography>

                        <Typography
                            sx={{
                                maxWidth: 720,
                                fontFamily: '"Nunito Sans", sans-serif',
                                fontSize: {
                                    xs: 16,
                                    md: 17,
                                },
                                lineHeight: 1.75,
                                color: colors.muted,
                            }}
                        >
                            Pflege Titel, Text, Beitragsbild und Sichtbarkeit. Bilder
                            werden direkt in Supabase Storage gespeichert.
                        </Typography>
                    </Box>

                    <Stack
                        spacing={1}
                        sx={{
                            alignItems: {
                                xs: "stretch",
                                md: "flex-end",
                            },
                        }}
                    >
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={isSubmitting || isUploadingImage}
                            sx={{
                                minHeight: 54,
                                px: 3.3,
                                borderRadius: 999,
                                backgroundColor: colors.brownDark,
                                color: "#fff",
                                fontFamily: '"Nunito Sans", sans-serif',
                                fontWeight: 900,
                                textTransform: "uppercase",
                                letterSpacing: "0.07em",
                                boxShadow: "0 14px 30px rgba(111,81,54,0.22)",

                                "&:hover": {
                                    backgroundColor: colors.brownDarker,
                                    boxShadow:
                                        "0 18px 38px rgba(111,81,54,0.28)",
                                },

                                "&:disabled": {
                                    color: "rgba(255,255,255,0.78)",
                                    backgroundColor: "rgba(111,81,54,0.62)",
                                },
                            }}
                        >
                            {isSubmitting || isUploadingImage
                                ? "Speichert..."
                                : "Speichern"}
                        </Button>

                        <Button
                            type="button"
                            onClick={() => navigate("/admin/news")}
                            variant="text"
                            disabled={isSubmitting || isUploadingImage}
                            sx={{
                                borderRadius: 999,
                                color: colors.brownDark,
                                fontFamily: '"Nunito Sans", sans-serif',
                                fontWeight: 900,
                                textTransform: "none",

                                "&:hover": {
                                    backgroundColor: "rgba(155,118,83,0.08)",
                                },
                            }}
                        >
                            Zurück zur Übersicht
                        </Button>
                    </Stack>
                </Box>
            </Paper>

            {errorMessage && (
                <Alert
                    severity="error"
                    sx={{
                        borderRadius: 3,
                        backgroundColor: "rgba(211,47,47,0.08)",
                        fontFamily: '"Nunito Sans", sans-serif',
                        "& .MuiAlert-message": {
                            fontWeight: 700,
                        },
                    }}
                >
                    {errorMessage}
                </Alert>
            )}

            {(isSubmitting || isUploadingImage) && (
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        borderRadius: 4,
                        backgroundColor: colors.paper,
                        border: `1px solid ${colors.border}`,
                    }}
                >
                    <Typography
                        sx={{
                            mb: 1,
                            fontFamily: '"Nunito Sans", sans-serif',
                            fontSize: 14,
                            fontWeight: 900,
                            color: colors.brownDark,
                        }}
                    >
                        {isUploadingImage
                            ? "Bild wird hochgeladen..."
                            : "Beitrag wird gespeichert..."}
                    </Typography>

                    <LinearProgress
                        sx={{
                            height: 8,
                            borderRadius: 999,
                            backgroundColor: "rgba(155,118,83,0.12)",
                            "& .MuiLinearProgress-bar": {
                                backgroundColor: colors.brownDark,
                            },
                        }}
                    />
                </Paper>
            )}

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr",
                        lg: "1.25fr 0.75fr",
                    },
                    gap: 3,
                    alignItems: "start",
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        p: {
                            xs: 3,
                            md: 4,
                        },
                        borderRadius: 5,
                        backgroundColor: colors.paper,
                        border: `1px solid ${colors.border}`,
                        boxShadow: "0 18px 60px rgba(78,54,30,0.08)",
                    }}
                >
                    <Typography
                        component="h2"
                        sx={{
                            mb: 2.5,
                            fontFamily: '"Quicksand", sans-serif',
                            fontSize: {
                                xs: "1.9rem",
                                md: "2.35rem",
                            },
                            lineHeight: 1,
                            fontWeight: 800,
                            letterSpacing: "-0.055em",
                            color: colors.ink,
                        }}
                    >
                        Beitragsdaten
                    </Typography>

                    <Stack spacing={2.4}>
                        <TextField
                            label="Titel"
                            value={form.title}
                            onChange={(event) =>
                                handleTitleChange(event.target.value)
                            }
                            required
                            fullWidth
                            disabled={isSubmitting || isUploadingImage}
                            sx={fieldStyles}
                        />

                        <TextField
                            label="Slug"
                            value={form.slug}
                            onChange={(event) =>
                                updateField(
                                    "slug",
                                    createSlug(event.target.value)
                                )
                            }
                            required
                            fullWidth
                            disabled={isSubmitting || isUploadingImage}
                            helperText="Wird später für die URL verwendet, z. B. neue-fruehjahrsmode"
                            sx={fieldStyles}
                        />

                        <TextField
                            label="Kurzbeschreibung"
                            value={form.excerpt}
                            onChange={(event) =>
                                updateField("excerpt", event.target.value)
                            }
                            multiline
                            minRows={3}
                            fullWidth
                            disabled={isSubmitting || isUploadingImage}
                            helperText="Kurz und knackig. Ideal für Vorschauen und Übersichten."
                            sx={fieldStyles}
                        />

                        <TextField
                            label="Inhalt"
                            value={form.content}
                            onChange={(event) =>
                                updateField("content", event.target.value)
                            }
                            multiline
                            minRows={9}
                            fullWidth
                            disabled={isSubmitting || isUploadingImage}
                            sx={fieldStyles}
                        />
                    </Stack>
                </Paper>

                <Stack spacing={3}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: {
                                xs: 3,
                                md: 4,
                            },
                            borderRadius: 5,
                            backgroundColor: colors.paper,
                            border: `1px solid ${colors.border}`,
                            boxShadow: "0 18px 60px rgba(78,54,30,0.08)",
                        }}
                    >
                        <Typography
                            component="h2"
                            sx={{
                                mb: 1,
                                fontFamily: '"Quicksand", sans-serif',
                                fontSize: {
                                    xs: "1.9rem",
                                    md: "2.2rem",
                                },
                                lineHeight: 1,
                                fontWeight: 800,
                                letterSpacing: "-0.055em",
                                color: colors.ink,
                            }}
                        >
                            Beitragsbild
                        </Typography>

                        <Typography
                            sx={{
                                mb: 2.5,
                                fontFamily: '"Nunito Sans", sans-serif',
                                fontSize: 14,
                                lineHeight: 1.6,
                                color: colors.muted,
                            }}
                        >
                            Lade ein JPG, PNG oder WebP hoch. Maximal 5 MB.
                        </Typography>

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            onChange={handleImageChange}
                            style={{ display: "none" }}
                        />

                        <Box
                            sx={{
                                mb: 2,
                                overflow: "hidden",
                                borderRadius: 4,
                                border: `1px solid ${colors.border}`,
                                backgroundColor: "rgba(255,255,255,0.52)",
                                aspectRatio: "16 / 10",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            {imagePreviewUrl ? (
                                <Box
                                    component="img"
                                    src={imagePreviewUrl}
                                    alt="Beitragsbild Vorschau"
                                    sx={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        display: "block",
                                    }}
                                />
                            ) : (
                                <Stack
                                    spacing={1}
                                    alignItems="center"
                                    sx={{ px: 3, textAlign: "center" }}
                                >
                                    <Typography
                                        aria-hidden="true"
                                        sx={{ fontSize: 34, lineHeight: 1 }}
                                    >
                                        🖼️
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontFamily:
                                                '"Nunito Sans", sans-serif',
                                            fontSize: 14,
                                            fontWeight: 900,
                                            color: colors.softMuted,
                                        }}
                                    >
                                        Noch kein Bild ausgewählt
                                    </Typography>
                                </Stack>
                            )}
                        </Box>

                        <Stack
                            direction={{
                                xs: "column",
                                sm: "row",
                                lg: "column",
                            }}
                            spacing={1}
                        >
                            <Button
                                type="button"
                                variant="outlined"
                                disabled={isSubmitting || isUploadingImage}
                                onClick={() => fileInputRef.current?.click()}
                                sx={{
                                    minHeight: 46,
                                    borderRadius: 999,
                                    borderWidth: 2,
                                    borderColor: colors.brown,
                                    color: colors.brownDark,
                                    fontFamily: '"Nunito Sans", sans-serif',
                                    fontWeight: 900,
                                    textTransform: "none",

                                    "&:hover": {
                                        borderWidth: 2,
                                        borderColor: colors.brownDark,
                                        backgroundColor:
                                            "rgba(155,118,83,0.08)",
                                    },
                                }}
                            >
                                Bild auswählen
                            </Button>

                            {imagePreviewUrl && (
                                <Button
                                    type="button"
                                    variant="text"
                                    disabled={isSubmitting || isUploadingImage}
                                    onClick={handleRemoveImage}
                                    sx={{
                                        minHeight: 46,
                                        borderRadius: 999,
                                        color: colors.danger,
                                        fontFamily:
                                            '"Nunito Sans", sans-serif',
                                        fontWeight: 900,
                                        textTransform: "none",

                                        "&:hover": {
                                            backgroundColor: colors.dangerBg,
                                        },
                                    }}
                                >
                                    Bild entfernen
                                </Button>
                            )}
                        </Stack>

                        <Divider
                            sx={{
                                my: 2.5,
                                borderColor: "rgba(42,38,33,0.1)",
                            }}
                        />

                        <TextField
                            label="Bild-URL"
                            value={form.image_url}
                            onChange={(event) => {
                                setSelectedImageFile(null);

                                if (localPreviewUrl) {
                                    URL.revokeObjectURL(localPreviewUrl);
                                    setLocalPreviewUrl("");
                                }

                                updateField("image_url", event.target.value);
                            }}
                            fullWidth
                            disabled={isSubmitting || isUploadingImage}
                            helperText="Optional: Du kannst alternativ auch eine externe Bild-URL einfügen."
                            sx={fieldStyles}
                        />
                    </Paper>

                    <Paper
                        elevation={0}
                        sx={{
                            p: {
                                xs: 3,
                                md: 4,
                            },
                            borderRadius: 5,
                            backgroundColor: colors.paper,
                            border: `1px solid ${colors.border}`,
                            boxShadow: "0 18px 60px rgba(78,54,30,0.08)",
                        }}
                    >
                        <Typography
                            component="h2"
                            sx={{
                                mb: 1,
                                fontFamily: '"Quicksand", sans-serif',
                                fontSize: {
                                    xs: "1.9rem",
                                    md: "2.2rem",
                                },
                                lineHeight: 1,
                                fontWeight: 800,
                                letterSpacing: "-0.055em",
                                color: colors.ink,
                            }}
                        >
                            Veröffentlichung
                        </Typography>

                        <Typography
                            sx={{
                                mb: 2,
                                fontFamily: '"Nunito Sans", sans-serif',
                                fontSize: 14,
                                lineHeight: 1.6,
                                color: colors.muted,
                            }}
                        >
                            Entwürfe sind vorbereitet, aber noch nicht öffentlich
                            sichtbar.
                        </Typography>

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.published}
                                    disabled={isSubmitting || isUploadingImage}
                                    onChange={(event) =>
                                        updateField(
                                            "published",
                                            event.target.checked
                                        )
                                    }
                                    sx={{
                                        "& .MuiSwitch-switchBase.Mui-checked": {
                                            color: colors.success,
                                        },
                                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                            {
                                                backgroundColor:
                                                colors.success,
                                            },
                                    }}
                                />
                            }
                            label={
                                <Typography
                                    sx={{
                                        fontFamily:
                                            '"Nunito Sans", sans-serif',
                                        fontWeight: 900,
                                        color: form.published
                                            ? colors.success
                                            : colors.brownDark,
                                    }}
                                >
                                    {form.published
                                        ? "Veröffentlicht"
                                        : "Als Entwurf speichern"}
                                </Typography>
                            }
                        />
                    </Paper>
                </Stack>
            </Box>

            <Paper
                elevation={0}
                sx={{
                    p: {
                        xs: 2.5,
                        md: 3,
                    },
                    borderRadius: 5,
                    backgroundColor: colors.paper,
                    border: `1px solid ${colors.border}`,
                    display: "flex",
                    flexDirection: {
                        xs: "column",
                        sm: "row",
                    },
                    justifyContent: "space-between",
                    gap: 1.5,
                }}
            >
                <Button
                    type="button"
                    onClick={() => navigate("/admin/news")}
                    variant="outlined"
                    disabled={isSubmitting || isUploadingImage}
                    sx={{
                        minHeight: 52,
                        px: 3.5,
                        borderRadius: 999,
                        borderWidth: 2,
                        borderColor: colors.brown,
                        color: colors.brownDark,
                        fontFamily: '"Nunito Sans", sans-serif',
                        fontSize: 14,
                        fontWeight: 900,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",

                        "&:hover": {
                            borderWidth: 2,
                            borderColor: colors.brownDark,
                            backgroundColor: "rgba(155,118,83,0.08)",
                        },
                    }}
                >
                    Abbrechen
                </Button>

                <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting || isUploadingImage}
                    sx={{
                        minHeight: 52,
                        px: 3.5,
                        borderRadius: 999,
                        backgroundColor: colors.brownDark,
                        color: "#fff",
                        fontFamily: '"Nunito Sans", sans-serif',
                        fontSize: 14,
                        fontWeight: 900,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        boxShadow: "0 14px 30px rgba(111,81,54,0.22)",

                        "&:hover": {
                            backgroundColor: colors.brownDarker,
                            boxShadow:
                                "0 18px 38px rgba(111,81,54,0.28)",
                        },

                        "&:disabled": {
                            color: "rgba(255,255,255,0.78)",
                            backgroundColor: "rgba(111,81,54,0.62)",
                        },
                    }}
                >
                    {isSubmitting || isUploadingImage
                        ? "Speichert..."
                        : "Speichern"}
                </Button>
            </Paper>
        </Box>
    );
}

const fieldStyles = {
    "& .MuiOutlinedInput-root": {
        borderRadius: 3,
        backgroundColor: "rgba(255,255,255,0.72)",
        fontFamily: '"Nunito Sans", sans-serif',

        "& fieldset": {
            borderColor: "rgba(42,38,33,0.14)",
        },

        "&:hover fieldset": {
            borderColor: "rgba(155,118,83,0.45)",
        },

        "&.Mui-focused fieldset": {
            borderColor: colors.brown,
            borderWidth: 2,
        },
    },

    "& .MuiInputLabel-root": {
        fontFamily: '"Nunito Sans", sans-serif',
        fontWeight: 700,
    },

    "& .MuiFormHelperText-root": {
        fontFamily: '"Nunito Sans", sans-serif',
    },
};

export default AdminNewsForm;